import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { z } from 'zod';
import type {
  Bilingue,
  creationEvenementSchema,
  DemandeInscriptionRegie,
  messageEvenementSchema,
  ETATS_INSCRIPTION,
  TYPES_EVENEMENT,
} from '@artosera/shared';
import { api, requeteTelechargement, type AppelApi } from './client.js';
import { appelDeRequete, cleMetier, dansLaPortee } from './cles.js';
import { useMutation } from './mutation.js';

export type CorpsEvenement = z.input<typeof creationEvenementSchema>;
export type CorpsMessage = z.input<typeof messageEvenementSchema>;
export interface EvenementPresente {
  id: string;
  numero: string;
  type: (typeof TYPES_EVENEMENT)[number];
  titre: Bilingue;
  description?: Bilingue;
  messageConfirmation?: Bilingue;
  expositionId: string | null;
  debut?: string | null;
  fin?: string | null;
  dates: { debut: string | null; fin: string | null };
  lieuId: string | null;
  adresseLibre?: string;
  jauge: number | null;
  placesParInscriptionMax: number;
  listeAttente: boolean;
  acces: 'public' | 'sur_lien';
  rappel: 'aucun' | '24h' | '48h';
  inscriptionsDesLe?: string | null;
  inscriptionsJusquAu?: string | null;
  etat: 'brouillon' | 'publie' | 'annule';
  phase:
    | 'brouillon'
    | 'annule'
    | 'sans_date'
    | 'clos'
    | 'en_cours'
    | 'inscriptions_ouvertes'
    | 'a_venir';
  placesConfirmees: number;
  placesEnAttente: number;
  lienPublic: string;
  version: number;
}
export interface InscriptionPresentee {
  id: string;
  evenementId: string;
  contactId: string;
  nom: string;
  email: string;
  places: number;
  etat: (typeof ETATS_INSCRIPTION)[number];
  rangAttente: number | null;
  presentLe: string | null;
  source: 'site' | 'regie';
  contactEfface: boolean;
}
export interface ResultatEmargement {
  resultat: 'admis' | 'deja_emarge' | 'inconnu' | 'annulee' | 'liste_attente' | 'hors_horaire';
  inscription?: InscriptionPresentee;
  presentLe?: string | null;
}
export interface MessageEvenement extends CorpsMessage {
  _id: string;
  creeLe: string;
}
export interface FiltresEvenements {
  periode?: 'a_venir' | 'passes';
  expositionId?: string;
}
export interface FiltresInscrits {
  etat?: InscriptionPresentee['etat'];
  q?: string;
}
const base = (g: string) => `/g/${encodeURIComponent(g)}/evenements`;
const fiche = (g: string, id: string) => `${base(g)}/${encodeURIComponent(id)}`;
function query(filtres: object) {
  const params = new URLSearchParams();
  for (const [cle, valeur] of Object.entries(filtres))
    if (valeur !== undefined && valeur !== '') params.set(cle, String(valeur));
  return params.toString();
}
export const evenementsApi = {
  lister: (g: string, filtres: FiltresEvenements, appel?: AppelApi) =>
    api.get<{ evenements: EvenementPresente[] }>(`${base(g)}?${query(filtres)}`, {
      galerie: g,
      ...appel,
    }),
  lire: (g: string, id: string, appel?: AppelApi) =>
    api.get<{ evenement: EvenementPresente }>(fiche(g, id), { galerie: g, ...appel }),
  creer: (g: string, corps: CorpsEvenement, appel?: AppelApi) =>
    api.post<{ evenement: EvenementPresente }>(base(g), corps, { galerie: g, ...appel }),
  modifier: (
    g: string,
    id: string,
    corps: Partial<CorpsEvenement> & { version: number },
    appel?: AppelApi,
  ) => api.patch<{ evenement: EvenementPresente }>(fiche(g, id), corps, { galerie: g, ...appel }),
  geste: (
    g: string,
    id: string,
    geste: 'publier' | 'annuler' | 'regenerer-lien',
    appel?: AppelApi,
  ) =>
    api.post<{ evenement: EvenementPresente }>(
      `${fiche(g, id)}/${geste}`,
      {},
      { galerie: g, ...appel },
    ),
  inscriptions: (g: string, id: string, filtres: FiltresInscrits, appel?: AppelApi) =>
    api.get<{ inscriptions: InscriptionPresentee[] }>(
      `${fiche(g, id)}/inscriptions?${query(filtres)}`,
      { galerie: g, ...appel },
    ),
  contact: (g: string, contactId: string, appel?: AppelApi) =>
    api.get<{ inscriptions: InscriptionPresentee[] }>(
      `${base(g)}/inscriptions?${query({ contactId })}`,
      { galerie: g, ...appel },
    ),
  inscrire: (g: string, id: string, corps: DemandeInscriptionRegie, appel?: AppelApi) =>
    api.post<{ inscription: InscriptionPresentee }>(`${fiche(g, id)}/inscriptions`, corps, {
      galerie: g,
      ...appel,
    }),
  billet: (
    g: string,
    id: string,
    iid: string,
    geste: 'annuler' | 'confirmer' | 'renvoyer' | 'regenerer',
    appel?: AppelApi,
  ) =>
    api.post<{ inscription: InscriptionPresentee }>(
      `${fiche(g, id)}/inscriptions/${encodeURIComponent(iid)}/${geste}`,
      {},
      { galerie: g, ...appel },
    ),
  emarger: (g: string, id: string, code: string, appel?: AppelApi) =>
    api.post<ResultatEmargement>(`${fiche(g, id)}/emargement`, { code }, { galerie: g, ...appel }),
  presence: async (
    g: string,
    id: string,
    iid: string,
    presente: boolean,
    appel?: AppelApi,
  ): Promise<ResultatEmargement | { inscription: InscriptionPresentee }> => {
    const chemin = `${fiche(g, id)}/inscriptions/${encodeURIComponent(iid)}/presence`;
    if (presente) return api.post<ResultatEmargement>(chemin, {}, { galerie: g, ...appel });
    return api.delete<{ inscription: InscriptionPresentee }>(chemin, { galerie: g, ...appel });
  },
  messages: (g: string, id: string, appel?: AppelApi) =>
    api.get<{ messages: MessageEvenement[] }>(`${fiche(g, id)}/messages`, { galerie: g, ...appel }),
  envoyer: (g: string, id: string, corps: CorpsMessage, appel?: AppelApi) =>
    api.post<{ message: MessageEvenement }>(`${fiche(g, id)}/messages`, corps, {
      galerie: g,
      ...appel,
    }),
  telecharger: (g: string, id: string, fichier: 'qr.svg' | 'inscriptions.csv', appel: AppelApi) =>
    requeteTelechargement(`${fiche(g, id)}/${fichier}`, { galerie: g, ...appel }),
};
export function useEvenements(g: string, filtres: FiltresEvenements = {}, enabled = true) {
  return useQuery({
    queryKey: cleMetier(g, 'evenements', 'liste', filtres),
    queryFn: (c) => evenementsApi.lister(g, filtres, appelDeRequete(c)),
    enabled,
  });
}
export function useEvenement(g: string, id: string, enabled = true) {
  return useQuery({
    queryKey: cleMetier(g, 'evenements', 'fiche', id),
    queryFn: (c) => evenementsApi.lire(g, id, appelDeRequete(c)),
    enabled,
  });
}
export function useInscrits(g: string, id: string, filtres: FiltresInscrits = {}, enabled = true) {
  return useQuery({
    queryKey: cleMetier(g, 'evenements', 'inscrits', id, filtres),
    queryFn: (c) => evenementsApi.inscriptions(g, id, filtres, appelDeRequete(c)),
    enabled,
  });
}
export function useInscriptionsContact(g: string, contactId: string) {
  return useQuery({
    queryKey: cleMetier(g, 'evenements', 'contact', contactId),
    queryFn: (c) => evenementsApi.contact(g, contactId, appelDeRequete(c)),
  });
}
export function useMessagesEvenement(g: string, id: string) {
  return useQuery({
    queryKey: cleMetier(g, 'evenements', 'messages', id),
    queryFn: (c) => evenementsApi.messages(g, id, appelDeRequete(c)),
  });
}
export function useActionEvenement<T, V>(
  g: string,
  action: (corps: V, appel: AppelApi) => Promise<T>,
) {
  const client = useQueryClient();
  return useMutation(
    dansLaPortee({
      mutationFn: action,
      onSettled: () => client.invalidateQueries({ queryKey: cleMetier(g, 'evenements') }),
    }),
  );
}
